import {
  Activity,
  Building2,
  CheckCircle2,
  ClipboardCheck,
  Cloud,
  CloudCog,
  Database,
  DollarSign,
  ExternalLink,
  Gauge,
  Globe,
  HardHat,
  Home,
  ShieldCheck,
  Smartphone,
  UserCheck,
  Users,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { type MetricsResponse, Permission } from '@metron/shared';
import type { MetricsYDomainMode } from '../components/MetricsChart.tsx';
import { sla1dColorClass, sla3dColorClass } from '../lib/utils.ts';

export type Granularity = 'hourly' | 'daily' | 'weekly';

/**
 * Dashboard tab a card renders on (MET-3255). Placement rule: end-user
 * activity (WAU/MAU) goes to Users, costs go to Spend, company revenue goes
 * to Revenue, and platform figures such as customers, meters and read rates
 * go to Home.
 */
export type DashboardTab = 'home' | 'users' | 'spend' | 'revenue';

export const DEFAULT_DASHBOARD_TAB: DashboardTab = 'home';

export interface DashboardTabDef {
  id: DashboardTab;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  /**
   * Permission required to see this tab; omit for tabs every dashboard
   * viewer gets. Same convention as the sidebar's NavigationItem.
   */
  requiredPermission?: Permission;
}

export const DASHBOARD_TABS: DashboardTabDef[] = [
  { id: 'home', label: 'Home', icon: Home },
  { id: 'users', label: 'Users' },
  // Dollars are Admin-tier only (MET-3333).
  { id: 'spend', label: 'Spend', requiredPermission: Permission.VIEW_SPEND_METRICS },
  // Same Admin-tier gate as Spend (MET-3599); the API also omits revenue for others.
  { id: 'revenue', label: 'Revenue', requiredPermission: Permission.VIEW_SPEND_METRICS },
];

const WAU_DESCRIPTION =
  'Distinct external users active in the trailing 7 days (weekly active users).';
const MAU_DESCRIPTION =
  'Distinct external users active in the trailing 30 days (monthly active users).';
const STAFF_WAU_DESCRIPTION =
  'Distinct internal (staff) users active in the trailing 7 days (weekly active users).';
const STAFF_MAU_DESCRIPTION =
  'Distinct internal (staff) users active in the trailing 30 days (monthly active users).';
const ACCOUNTS_WAU_DESCRIPTION =
  'Distinct customer accounts with at least one external user active in the trailing 7 days (weekly active accounts).';
const ACCOUNTS_MAU_DESCRIPTION =
  'Distinct customer accounts with at least one external user active in the trailing 30 days (monthly active accounts).';
// WaterScope Plus is sourced from the Cognito login history, not an activity
// log, so its cards say "with a sign-in" where the Azure-hosted ones say
// "active".
const PLUS_WAU_DESCRIPTION =
  'Distinct external users with a sign-in in the trailing 7 days (weekly active users). Internal shown, not counted.';
const PLUS_MAU_DESCRIPTION =
  'Distinct external users with a sign-in in the trailing 30 days (monthly active users).';

export interface DashboardMetricCard {
  key: string;
  tab: DashboardTab;
  title: string;
  icon: ComponentType<{ className?: string }>;
  description: string;
  metricId: string;
  granularity: Granularity;
  showHistory: boolean;
  /**
   * History-modal y-axis mode. Default 'tight' (fit to the data so
   * month-over-month movement is visible; the floor never drops below 0 for
   * non-negative data). Set 'zero' to force a zero baseline.
   */
  yDomainMode?: MetricsYDomainMode;
  /**
   * Missing values (null/undefined — e.g. an as-of day with no stored row)
   * must render as '—', never a confident 0: a plausible wrong number on a
   * KPI slide is worse than an explicit blank.
   */
  getValue: (metrics: MetricsResponse | null) => number | string;
  getTimestamp: (metrics: MetricsResponse | null) => string | null | undefined;
  getValueClassName?: (metrics: MetricsResponse | null) => string | undefined;
  /**
   * Optional sub-value breakdown rendered under the main value (e.g. customer
   * count by account type). Entries with an undefined value are hidden.
   */
  getBreakdown?: (
    metrics: MetricsResponse | null,
  ) => { label: string; value: number | undefined; note?: string }[];
  /**
   * Formats numeric values (headline, sparkline hi/lo, chart tooltip). Keep
   * getValue numeric and format here — a pre-formatted string value would
   * suppress the delta chip.
   */
  valueFormatter?: (value: number) => string;
  /**
   * Reference values drawn as dashed lines on the history-modal chart. Same
   * basis as the charted series — pair monthly values with `rollingSumDays`.
   */
  baseline?: number;
  target?: number;
  /** Chart the trailing N-day sum in the history modal instead of raw dailies. */
  rollingSumDays?: number;
  /** Invert delta-chip colors for metrics where a decrease is good (e.g. spend). */
  lowerIsBetter?: boolean;
}

/**
 * Objective 2 baseline and target for the Azure spend card (FINALIZED by
 * leadership): January 2026 amortized spend, Production + QA subscriptions.
 * From az CLI (AmortizedCost, 2026-01-01..2026-01-31): Production $27,512.21
 * + QA $15,283.15. Target is baseline × 0.75 (the −25% goal). Monthly USD —
 * the card headline and history chart are rolling-30-day totals on the same
 * basis. A restated January or a re-based target is a one-line change here.
 */
const AZURE_SPEND_BASELINE_MONTH = '2026-01';
const AZURE_SPEND_BASELINE_MONTHLY_USD = 42_795.36;
const AZURE_SPEND_TARGET_MONTHLY_USD = Math.round(AZURE_SPEND_BASELINE_MONTHLY_USD * 0.75 * 100) /
  100; // $32,096.52

/**
 * Working title for the AWS spend card (MET-3318), not yet settled: "WaterScope
 * Plus Infra Spend" misled Erin, and her suggestion stands until Brendon
 * calls it. One constant so the title changes in one place; tests read it.
 */
export const AWS_INFRA_SPEND_TITLE = 'AWS Infra Spend (30d)';

export const formatUsd = (value: number): string =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });

/** "Sep 2026" for a month's ISO periodStart, in UTC so it never shifts a month. */
export const formatMonth = (periodStart: string): string =>
  new Date(periodStart).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

export const DASHBOARD_METRICS: DashboardMetricCard[] = [
  {
    key: 'total-customers',
    tab: 'home',
    title: 'Total Customers',
    icon: Users,
    description:
      'Total customers (Salesforce closed-won accounts, deduplicated to operating entities)',
    metricId: 'customer_count_daily',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.customerCount ?? '—',
    getTimestamp: (m) => m?.computedAt?.customerCount,
    getBreakdown: (m) => [
      { label: 'MHP', value: m?.customerCountMhp },
      { label: 'CMFI', value: m?.customerCountCmfi },
      { label: 'Utility', value: m?.customerCountUtility },
    ],
  },
  {
    key: 'read-rate-1d',
    tab: 'home',
    title: '24-Hour Read Rate',
    icon: CheckCircle2,
    description:
      'Percentage of active meters (seen in last 30 days) that reported data yesterday. Target: ≥95%.',
    metricId: 'sensor_reporting_pct_daily',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.sensorReportingPct != null ? `${m.sensorReportingPct}%` : '—',
    getTimestamp: (m) => m?.computedAt?.sensorReportingPct,
    getValueClassName: (m) => sla1dColorClass(m?.sensorReportingPct ?? null),
  },
  {
    key: 'read-rate-3d',
    tab: 'home',
    title: '3-Day Read Rate',
    icon: ClipboardCheck,
    description:
      'Percentage of active meters (seen in last 30 days) that reported data within the last 3 days. Target: ≥97%.',
    metricId: 'sensor_reporting_pct_3d',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.sensorReportingPct3d != null ? `${m.sensorReportingPct3d}%` : '—',
    getTimestamp: (m) => m?.computedAt?.sensorReportingPct3d,
    getValueClassName: (m) => sla3dColorClass(m?.sensorReportingPct3d ?? null),
  },
  {
    key: 'data-points-7d-avg',
    tab: 'home',
    title: 'Daily Data Points',
    icon: Database,
    description:
      'Rolling 7-day daily average of all data points received from all meters reporting into the system',
    metricId: 'data_points_daily',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.dataPointsAvg7d ?? '—',
    getTimestamp: (m) => m?.computedAt?.dataPointsAvg7d,
  },
  {
    key: 'active-meters-30d',
    tab: 'home',
    title: 'Active Meters',
    icon: Activity,
    description: 'Number of unique meters that have reported data within the last 30 days',
    metricId: 'sensors_updated_unique_30d',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.sensorsUpdatedAvg30d ?? '—',
    getTimestamp: (m) => m?.computedAt?.sensorsUpdatedAvg30d,
  },
  {
    key: 'total-meters',
    tab: 'home',
    title: 'Total Meters',
    icon: Gauge,
    description: 'Total unique meters that have ever reported data into the system',
    metricId: 'meter_count_daily',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.meterCount ?? '—',
    getTimestamp: (m) => m?.computedAt?.meterCount,
  },
  {
    key: 'external-meters',
    tab: 'home',
    title: 'External Meters',
    icon: ExternalLink,
    description: 'Count of distinct external (non-Metron) meters reporting readings',
    metricId: 'external_meter_count_daily',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.externalMeterCount ?? '—',
    getTimestamp: (m) => m?.computedAt?.externalMeterCount,
  },
  // Objective 2 (MET-3165): Metron's own Azure bill — infrastructure cost,
  // not customer cost. Headline total only; drill-downs live in Turntable.
  // Headline and history chart are rolling-30-day amortized totals so they
  // sit on the same monthly basis as the baseline/target lines; the inline
  // sparkline trends the raw daily series.
  {
    key: 'azure-infra-spend',
    tab: 'spend',
    title: 'Azure Infra Spend (30d)',
    icon: CloudCog,
    description:
      'Rolling 30-day amortized Azure infrastructure spend, Production + QA subscriptions. ' +
      `Dashed lines: ${AZURE_SPEND_BASELINE_MONTH} baseline ${
        formatUsd(AZURE_SPEND_BASELINE_MONTHLY_USD)
      }/mo and −25% target ${formatUsd(AZURE_SPEND_TARGET_MONTHLY_USD)}/mo. ` +
      'Last few days may shift as Azure finalizes billing.',
    metricId: 'azure_infra_spend_daily',
    granularity: 'daily',
    showHistory: true,
    // '—' (not 0) when the API has no history yet: a confident $0 for
    // infrastructure spend would read as a real number on a screenshot.
    getValue: (m) => m?.azureSpend30d ?? '—',
    getTimestamp: (m) => m?.computedAt?.azureSpend30d,
    valueFormatter: formatUsd,
    baseline: AZURE_SPEND_BASELINE_MONTHLY_USD,
    target: AZURE_SPEND_TARGET_MONTHLY_USD,
    rollingSumDays: 30,
    lowerIsBetter: true,
  },
  // AWS infrastructure spend (MET-3318): the AWS Plus account (the whole
  // account — Wellspring, the MET and other tools included) plus the
  // Timescale database, from the daily cost export. Same rolling-30-day
  // shape as the Azure card, but no baseline or target (the −25% goal is
  // Azure's), so the delta chip trends the sparkline instead. The AWS-only
  // and Timescale-only series are stored but have no card.
  {
    key: 'aws-infra-spend',
    tab: 'spend',
    title: AWS_INFRA_SPEND_TITLE,
    icon: Cloud,
    description: 'Rolling 30-day AWS infrastructure spend: the AWS Plus account (amortized) ' +
      'plus the Timescale database (realized cost).',
    metricId: 'aws_infra_spend_daily',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.awsSpend30d ?? '—',
    getTimestamp: (m) => m?.computedAt?.awsSpend30d,
    valueFormatter: formatUsd,
    rollingSumDays: 30,
    lowerIsBetter: true,
  },
  // Revenue tab (MET-3599): company-wide invoiced revenue from Epicor
  // (MET-3527). Monthly, so no sparkline or history modal yet (the modal tops
  // out at 90 days); the breakdown names each month instead.
  {
    key: 'revenue-monthly',
    tab: 'revenue',
    title: 'Revenue (Latest Month)',
    icon: DollarSign,
    description: 'Approximate invoiced revenue for the latest completed month, from Epicor ' +
      'invoice lines dated in the month, net of unapplied cash. Best effort, not booked ' +
      'revenue; recent months shift as late invoices and credit memos post.',
    metricId: 'epicor_revenue_monthly',
    // Placeholder: the metric is monthly, which the card's Granularity type
    // lacks. Unused while showHistory is false (no sparkline or modal fetch).
    granularity: 'daily',
    showHistory: false,
    getValue: (m) => m?.revenueMonthly?.[0]?.value ?? '—',
    // The months are labeled in the breakdown; a "Queried" stamp of the
    // month's start would misread as the query time.
    getTimestamp: () => undefined,
    getBreakdown: (m) =>
      (m?.revenueMonthly ?? []).map((month) => ({
        label: formatMonth(month.periodStart),
        value: month.value,
      })),
    valueFormatter: formatUsd,
  },
  // Users tab (MET-3310): fixed row order, each row WAU then MAU (D-043).
  // Card keys and metric IDs keep the backend 'classic' namespace; titles
  // use the sales-facing product names (D-038). External is the headline;
  // internal is shown but not summed (D-021). Admin Portal is staff-only,
  // so its cards headline internal users (D-035). The accounts row counts
  // distinct customer accounts, so it has no internal breakdown (D-042).
  {
    key: 'classic-webportal-accounts-7d',
    tab: 'users',
    title: 'WaterScope Classic Weekly Active Accounts',
    icon: Building2,
    description: ACCOUNTS_WAU_DESCRIPTION,
    metricId: 'classic_webportal_accounts_7d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicWebPortalAccounts7dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicWebPortalAccounts7dExternal,
  },
  {
    key: 'classic-webportal-accounts-30d',
    tab: 'users',
    title: 'WaterScope Classic Monthly Active Accounts',
    icon: Building2,
    description: ACCOUNTS_MAU_DESCRIPTION,
    metricId: 'classic_webportal_accounts_30d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicWebPortalAccounts30dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicWebPortalAccounts30dExternal,
  },
  {
    key: 'classic-webportal-users-7d',
    tab: 'users',
    title: 'WaterScope Classic Weekly Active Users (WAU)',
    icon: Globe,
    description: WAU_DESCRIPTION,
    metricId: 'classic_webportal_users_7d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicWebPortalUsers7dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicWebPortalUsers7dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.classicWebPortalUsers7dInternal,
      },
    ],
  },
  {
    key: 'classic-webportal-users-30d',
    tab: 'users',
    title: 'WaterScope Classic Monthly Active Users (MAU)',
    icon: Globe,
    description: MAU_DESCRIPTION,
    metricId: 'classic_webportal_users_30d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicWebPortalUsers30dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicWebPortalUsers30dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.classicWebPortalUsers30dInternal,
      },
    ],
  },
  // WaterScope Plus: the same external-headline / internal-not-counted shape
  // on the plus-login-users metrics, on the row below WaterScope Classic.
  {
    key: 'plus-users-7d',
    tab: 'users',
    title: 'WaterScope Plus WAU',
    icon: UserCheck,
    description: PLUS_WAU_DESCRIPTION,
    metricId: 'plus_users_7d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.plusUsers7dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.plusUsers7dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.plusUsers7dInternal,
      },
    ],
  },
  {
    key: 'plus-users-30d',
    tab: 'users',
    title: 'WaterScope Plus MAU',
    icon: UserCheck,
    description: PLUS_MAU_DESCRIPTION,
    metricId: 'plus_users_30d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.plusUsers30dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.plusUsers30dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.plusUsers30dInternal,
      },
    ],
  },
  {
    key: 'classic-fieldapp-users-7d',
    tab: 'users',
    title: 'Field App WAU',
    icon: HardHat,
    description: WAU_DESCRIPTION,
    metricId: 'classic_fieldapp_users_7d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicFieldAppUsers7dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicFieldAppUsers7dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.classicFieldAppUsers7dInternal,
      },
    ],
  },
  {
    key: 'classic-fieldapp-users-30d',
    tab: 'users',
    title: 'Field App MAU',
    icon: HardHat,
    description: MAU_DESCRIPTION,
    metricId: 'classic_fieldapp_users_30d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicFieldAppUsers30dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicFieldAppUsers30dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.classicFieldAppUsers30dInternal,
      },
    ],
  },
  {
    key: 'classic-consumerapp-users-7d',
    tab: 'users',
    title: 'Consumer App WAU',
    icon: Smartphone,
    description: WAU_DESCRIPTION,
    metricId: 'classic_consumerapp_users_7d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicConsumerAppUsers7dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicConsumerAppUsers7dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.classicConsumerAppUsers7dInternal,
      },
    ],
  },
  {
    key: 'classic-consumerapp-users-30d',
    tab: 'users',
    title: 'Consumer App MAU',
    icon: Smartphone,
    description: MAU_DESCRIPTION,
    metricId: 'classic_consumerapp_users_30d_external',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicConsumerAppUsers30dExternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicConsumerAppUsers30dExternal,
    getBreakdown: (m) => [
      {
        label: 'Internal',
        note: 'not included in total users',
        value: m?.classicConsumerAppUsers30dInternal,
      },
    ],
  },
  {
    key: 'classic-adminportal-users-7d',
    tab: 'users',
    title: 'Admin Portal WAU',
    icon: ShieldCheck,
    description: STAFF_WAU_DESCRIPTION,
    metricId: 'classic_adminportal_users_7d_internal',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicAdminPortalUsers7dInternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicAdminPortalUsers7dInternal,
  },
  {
    key: 'classic-adminportal-users-30d',
    tab: 'users',
    title: 'Admin Portal MAU',
    icon: ShieldCheck,
    description: STAFF_MAU_DESCRIPTION,
    metricId: 'classic_adminportal_users_30d_internal',
    granularity: 'daily',
    showHistory: true,
    getValue: (m) => m?.classicAdminPortalUsers30dInternal ?? '—',
    getTimestamp: (m) => m?.computedAt?.classicAdminPortalUsers30dInternal,
  },
];

/** The tabs the current user may see, in display order. */
export function visibleDashboardTabs(
  hasPermission: (permission: Permission) => boolean,
): DashboardTabDef[] {
  return DASHBOARD_TABS.filter(
    (tab) => tab.requiredPermission === undefined || hasPermission(tab.requiredPermission),
  );
}

/**
 * Resolve ?tab= against the tabs the user can see, so a shared
 * ?tab=spend link lands a non-admin on Home rather than on the spend cards.
 */
export function parseDashboardTab(
  value: string | null,
  tabs: readonly DashboardTabDef[] = DASHBOARD_TABS,
): DashboardTab {
  const match = tabs.find((tab) => tab.id === value);
  return match ? match.id : DEFAULT_DASHBOARD_TAB;
}

export function dashboardCardsForTab(tab: DashboardTab): DashboardMetricCard[] {
  return DASHBOARD_METRICS.filter((card) => card.tab === tab);
}
